/**
 * Options:
 *
 * - collection - FilesCollection
 */

import View from 'view/view'

import MediaUploadView from 'component/uploader/view/media_upload'
import MediaUploadListView from 'component/uploader/view/media_upload_list'
import FlashMessageView from 'component/flash_message/flash_message'
import template from 'component/uploader/template/container.tmpl'
import loadingIndicatorTemplate from 'component/uploader/template/media_upload_indicator.tmpl'

export default View.extend({
    template,
    loadingIndicatorTemplate,

    _subview: null,
    _lastErrorMessage: '',
    _isUploaded: false,
    _isResolvingFiles: false,

    bubbleEvents: {
        'MediaUpload:filePickerOpened': '_onFilePickerOpened',
        'MediaUpload:beforeFilesResolve': '_onBeforeFilesResolve',
        'MediaUpload:filesAdded': '_onFilesAdded',
        'MediaUpload:fileUploaded': '_onFileUploaded',
        'MediaUploadItem:remove': '_onItemRemoveClick',
        'MediaUpload:error': '_onError',
    },

    defaultOptions: {
        title: 'Add photos',
        limitOne: false,
    },

    initialize(options) {
        this.options = this.pickOptions(options, this.defaultOptions)

        this._subview = {
            uploader: null,
            list: null,
            flashMessage: null,
        }

        this.listenTo(this.collection, 'reset add remove change', this.render)
    },

    _onFilePickerOpened() {
        // clear up any existing error messages
        this._lastErrorMessage = ''
    },

    _onBeforeFilesResolve() {
        this._isResolvingFiles = true
        this.render()
    },

    _onFilesAdded({ files }) {
        this._isResolvingFiles = false
        this.collection.add(
            files.map((file) => ({
                fileId: file.id,
                isUploaded: false,
            }))
        )
    },

    _onFileUploaded({ file, response }) {
        this._isUploaded = true

        this.collection.findWhere({ fileId: file.id }).set({
            id: response.id,
            path: response.path,
            isUploaded: true,
        })
    },

    _onItemRemoveClick({ fileModel }) {
        this.collection.remove(fileModel)

        // Only files that were uploaded during the current session have a `fileId`.
        // Files that came from the server don't have a `fileId`, just an `id`.
        if (fileModel.get('fileId')) {
            this._subview.uploader.removeFile(fileModel.get('fileId'))
        }
    },

    _onError({ error }) {
        this._lastErrorMessage = error.message

        if (error.file) {
            const fileModel = this.collection.findWhere({ fileId: error.file.id })

            // sometimes there is no file model in the collection
            // for example, when you're trying to add same file twice - uploader triggers an error
            // that contains the file which is not in the collection
            if (fileModel) {
                fileModel.set({ error })
            }
        }

        this.render()
    },

    _renderFlashMessage(message) {
        this._subview.flashMessage = this.initSubview(FlashMessageView, {
            el: this.$('.js-flashmessage-component'),
            type: 'warning',
            message,
        }).render()

        return this._subview.flashMessage
    },

    render() {
        // Prevent uploader destroy/init on each render - new uploader instance will be created
        // Filter only exited views
        this.destroySubviews([this._subview.list, this._subview.flashMessage].filter(Boolean))

        this.$el.html(
            this.template({
                withGrid: !this.options.limitOne,
            })
        )

        if (this._lastErrorMessage) {
            this._renderFlashMessage(this._lastErrorMessage)
        }

        if (!this._subview.uploader) {
            this._subview.uploader = this.initSubview(MediaUploadView, {
                title: this.options.title,
                multi_selection: !this.options.limitOne,
            })
        }

        this._subview.list = this.initSubview(MediaUploadListView, {
            el: this.$('.js-uploader-container'),
            collection: this.collection,
            wrapperClass: '',
        }).render()

        if (this._isResolvingFiles) {
            this.$('.js-uploader-container').append(this.loadingIndicatorTemplate())
        }

        // If `single` mode is on, we don't render the uploader whenever there is already a photo in the list
        // (or if one is currently being resolved)
        if (this.options.limitOne && (this.collection.length > 0 || this._isResolvingFiles)) {
            return this
        }

        this.$('.js-uploader-container').prepend(this._subview.uploader.render().el)

        return this
    },
})
